import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CancelModal from '@/app/common/components/CancelModal';
import { forgetPromptRunBatch, trackerAiClient as api } from '@/app/TrackerAi/api/trackerAiClient';
import { isDuplicateExecutionError } from '@/app/TrackerAi/utils/api-errors';
import {
  enqueueRunErrorMessage,
  schedulePromptRunRefresh,
} from '@/app/TrackerAi/utils/prompt-run';
import { PromptsPageSkeleton } from '../components/common/Skeleton';
import { PageFilterBar } from '../components/filters/PageFilterBar';
import { DomainFilter } from '../components/filters/DomainFilter';
import { FilterChip, FilterMenuItem } from '../components/filters/FilterChip';
import { ModelFilter } from '../components/filters/ModelFilter';
import { TopicFilter } from '../components/filters/TopicFilter';
import { NoProject } from '../components/NoProject';
import {
  DuplicateExecutionNotice,
  ExecutionAlert,
} from '../components/executions/DuplicateExecutionNotice';
import { PromptInspector } from '../components/prompts/PromptInspector';
import { PromptsSummaryCards } from '../components/prompts/PromptsSummaryCards';
import { PromptRunProgress } from '../components/prompts/PromptRunProgress';
import { PromptBulkActions } from '../components/prompts/PromptBulkActions';
import { PromptsTable } from '../components/prompts/PromptsTable';
import { useProject } from '../context/ProjectContext';
import { useTrackerAiPath } from '../utils/paths';
import type { ProjectPrompt } from '../hooks/useProjectData';
import { getPromptBrandRows } from '../utils/prompt-domain-metrics';
import type { DuplicateExecutionData, UserBrandInsight } from '../types';

type TabFilter = 'all' | 'pending' | 'executed' | 'failed' | 'drafts';
type PendingDelete = { type: 'bulk' };

const TAB_OPTIONS: Array<[TabFilter, string]> = [
  ['all', 'همه'],
  ['pending', 'در انتظار'],
  ['executed', 'اجرا شده'],
  ['failed', 'ناموفق'],
  ['drafts', 'پیش‌نویس'],
];

function PromptFilterEmptyState({ onClear }: { onClear: () => void }) {
  return (
    <Card className="border-dashed shadow-none">
      <Card.Body className="py-5 text-center d-flex flex-column align-items-center">
        <span
          className="d-inline-flex align-items-center justify-content-center rounded-circle bg-body-tertiary text-primary mb-3"
          style={{ width: '2.75rem', height: '2.75rem' }}
          aria-hidden
        >
          <FontAwesomeIcon icon="filter" />
        </span>
        <p className="fs-0 fw-semibold text-800 mb-2">پرامپتی پیدا نشد</p>
        <p className="fs--1 text-500 mb-3" style={{ maxWidth: '28rem' }}>
          هیچ پرامپتی با فیلترهای انتخاب‌شده مطابقت ندارد. فیلترها را تغییر دهید یا پاک کنید.
        </p>
        <Button type="button" size="sm" variant="falcon-default" onClick={onClear}>
          پاک کردن فیلترها
        </Button>
      </Card.Body>
    </Card>
  );
}

export function PromptsPage() {
  return (
    <NoProject>
      <PromptsContent />
    </NoProject>
  );
}

function PromptsContent() {
  const {
    projectId,
    data,
    loading,
    error,
    setError,
    refresh,
    refreshPromptRow,
    defaultModelId,
    topicFilterId,
    setTopicFilterId,
    modelFilterId,
    setModelFilterId,
    domainFilterId,
    setDomainFilterId,
    setNewPromptOpen,
    runningPromptIds,
  } = useProject();
  const navigate = useNavigate();
  const toTrackerAi = useTrackerAiPath();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [runningId, setRunningId] = useState<string | null>(null);
  const [runningBulk, setRunningBulk] = useState(false);
  const [deletingBulk, setDeletingBulk] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [duplicateNotice, setDuplicateNotice] = useState<DuplicateExecutionData | null>(null);
  const [tab, setTab] = useState<TabFilter>('all');
  const [search, setSearch] = useState('');
  const pendingInspectIdRef = useRef<string | null>(null);

  const prompts = data?.prompts ?? [];
  const showTopic = !topicFilterId;

  useEffect(() => {
    const fromUrl = searchParams.get('prompt');
    if (fromUrl && prompts.some((p) => p.id === fromUrl)) {
      setSelectedId(fromUrl);
    }
  }, [prompts, searchParams]);

  const stats = useMemo(() => {
    const pending = prompts.filter((p) => p.status === 'pending').length;
    const executed = prompts.filter((p) => p.status === 'executed').length;
    const partial = prompts.filter((p) => p.status === 'partial').length;
    const failed = prompts.filter((p) => p.status === 'failed').length;
    const needsReview = prompts.filter(
      (p) => (p.status === 'executed' || p.status === 'partial') && p.brandsFound === 0,
    ).length;
    return { total: prompts.length, pending, executed: executed + partial, failed, needsReview };
  }, [prompts]);

  const filtered = useMemo(() => {
    return prompts.filter((p) => {
      if (tab === 'pending' && p.status !== 'pending') return false;
      if (tab === 'executed' && p.status !== 'executed' && p.status !== 'partial') return false;
      if (tab === 'failed' && p.status !== 'failed') return false;
      if (tab === 'drafts' && p.status !== 'draft') return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const inText = p.text.toLowerCase().includes(q);
        const inTopic = (p as ProjectPrompt).topicName?.toLowerCase().includes(q);
        const inBrand = getPromptBrandRows(p).some((brand) =>
          brand.canonicalName.toLowerCase().includes(q),
        );
        if (!inText && !inTopic && !inBrand) return false;
      }
      return true;
    });
  }, [prompts, tab, search]);

  const selected = prompts.find((p) => p.id === selectedId) ?? null;

  function selectPrompt(promptId: string) {
    setSelectedId(promptId);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('prompt', promptId);
        return next;
      },
      { replace: true },
    );
  }

  function closeInspector() {
    setSelectedId(null);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('prompt');
        return next;
      },
      { replace: true },
    );
  }

  function viewDuplicateResult(executionId: string) {
    setDuplicateNotice(null);
    void navigate(toTrackerAi('executions', `id=${encodeURIComponent(executionId)}`));
  }

  async function runPrompt(promptId: string) {
    const prompt = prompts.find((p) => p.id === promptId);
    const modelIds =
      prompt?.modelIds?.length
        ? prompt.modelIds
        : prompt?.modelId
          ? [prompt.modelId]
          : defaultModelId
            ? [defaultModelId]
            : [];
    if (!modelIds.length) {
      setError('قبل از اجرا، یک مدل AI در تنظیمات انتخاب کنید');
      return;
    }
    setRunningId(promptId);
    setError('');
    setDuplicateNotice(null);
    let queued = false;
    try {
      const result = await api.runPrompt(promptId);
      const enqueueError = enqueueRunErrorMessage(result);
      if (enqueueError) {
        setError(enqueueError);
      }
      queued = result.createdItems > 0;
      pendingInspectIdRef.current = promptId;
      await refreshPromptRow(promptId);
      schedulePromptRunRefresh(() => void refreshPromptRow(promptId));
      schedulePromptRunRefresh(() => void refresh({ silent: true }));
    } catch (e) {
      if (isDuplicateExecutionError(e)) {
        setDuplicateNotice(e.data);
        setError('');
        await refreshPromptRow(promptId);
        selectPrompt(promptId);
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
      await refreshPromptRow(promptId);
    } finally {
      if (!queued) setRunningId(null);
      await refresh({ silent: true });
    }
  }

  function requestDeleteSelected() {
    if (selectedIds.size === 0) return;
    setPendingDelete({ type: 'bulk' });
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeletingBulk(true);
    setError('');
    try {
      await api.deletePrompt([...selectedIds]);
      if (selectedId && selectedIds.has(selectedId)) {
        closeInspector();
      }
      setSelectedIds(new Set());
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeletingBulk(false);
    }
  }

  function handleRunProgressComplete() {
    const id = pendingInspectIdRef.current;
    pendingInspectIdRef.current = null;
    setRunningId(null);
    if (id) selectPrompt(id);
  }

  async function runSelected() {
    pendingInspectIdRef.current = null;
    const toRun = prompts.filter(
      (p) =>
        selectedIds.has(p.id) &&
        (p.status === 'pending' || p.status === 'failed' || p.status === 'draft'),
    );
    if (!toRun.length) return;
    setRunningBulk(true);
    setError('');
    setDuplicateNotice(null);
    try {
      const result = await api.enqueuePromptRuns({
        promptIds: toRun.map((p) => p.id),
      });
      const enqueueError = enqueueRunErrorMessage(result);
      if (enqueueError) {
        setError(enqueueError);
      }
      await refresh();
      schedulePromptRunRefresh(() => void refresh({ silent: true }));
    } catch (e) {
      if (isDuplicateExecutionError(e)) {
        setDuplicateNotice(e.data);
        setError('');
        await refresh();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunningId(null);
      setRunningBulk(false);
    }
  }

  const handleAddCompetitor = useCallback(
    async (brand: UserBrandInsight) => {
      if (!projectId) return;
      await api.createCompetitorFromExtracted(projectId, {
        brandName: brand.canonicalName ?? brand.brandName,
        detectedHost: brand.detectedHost ?? undefined,
      });
      await refresh();
    },
    [projectId, refresh],
  );

  const handleCreateCompetitor = useCallback(
    async (data: { brandName: string; websiteUrl: string; aliases: string[] }) => {
      if (!projectId) return;
      await api.createCompetitor(projectId, data);
      await refresh();
    },
    [projectId, refresh],
  );

  const selectedTabLabel = TAB_OPTIONS.find(([key]) => key === tab)?.[1] ?? 'همه';
  const hasActiveFilters = Boolean(
    search.trim() ||
      tab !== 'all' ||
      topicFilterId ||
      modelFilterId ||
      domainFilterId !== 'all',
  );
  function clearPromptFilters() {
    setSearch('');
    setTab('all');
    setTopicFilterId(null);
    setModelFilterId(null);
    setDomainFilterId('all');
  }

  const newPromptButton = (
    <Button
      variant="falcon-primary"
      size="sm"
      className="d-flex gap-2 align-items-center justify-content-center text-nowrap flex-shrink-0"
      onClick={() => setNewPromptOpen(true)}
    >
      <FontAwesomeIcon icon="plus" />
      پرامپت جدید
    </Button>
  );

  if (loading && prompts.length === 0) {
    return (
      <div>
        <div className="mb-4">
          <PageFilterBar
            showModels={false}
            showTopics={false}
            showDomains={false}
            splitDateAndFilters
            dateVariant="quick"
            actions={newPromptButton}
          />
        </div>
        <PromptsPageSkeleton />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4">
        <PageFilterBar
          showModels={false}
          showTopics={false}
          showDomains={false}
          splitDateAndFilters
          dateVariant="quick"
          actions={newPromptButton}
        />
      </div>

      <div className="mb-4">
        <PromptsSummaryCards {...stats} />
      </div>

      {duplicateNotice && (
        <div className="mb-3">
          <DuplicateExecutionNotice
            duplicate={duplicateNotice}
            onViewResult={() => viewDuplicateResult(duplicateNotice.existingRequestId)}
          />
        </div>
      )}

      {error && <ExecutionAlert message={error} />}

      <PromptRunProgress onComplete={handleRunProgressComplete} />

      <div
        className={`d-flex flex-wrap align-items-center gap-3 mb-3 w-100 ${
          filtered.length > 0 ? 'justify-content-between' : 'justify-content-end'
        }`}
      >
        <div className="order-2 d-flex flex-wrap align-items-center gap-2">
            <div className="position-relative" style={{ minWidth: '16rem', maxWidth: '22rem' }}>
              <FontAwesomeIcon
                icon="search"
                className="position-absolute top-50 translate-middle-y text-400"
                style={{ insetInlineStart: '0.75rem', pointerEvents: 'none' }}
              />
              <Form.Control
                type="search"
                size="sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="جستجوی پرامپت، تاپیک یا برند…"
                className="rounded-pill border-300"
                style={{ paddingInlineStart: '2.25rem' }}
              />
            </div>
            <FilterChip
              icon={<FontAwesomeIcon icon="filter" className="fs--2" />}
              label={tab === 'all' ? 'همه وضعیت‌ها' : selectedTabLabel}
              active={tab !== 'all'}
              align="end"
            >
              {TAB_OPTIONS.map(([key, label]) => (
                <FilterMenuItem key={key} selected={tab === key} onClick={() => setTab(key)}>
                  {key === 'all' ? 'همه وضعیت‌ها' : label}
                </FilterMenuItem>
              ))}
            </FilterChip>
            <ModelFilter />
            <TopicFilter />
            <DomainFilter />
            {hasActiveFilters && (
              <Button
                type="button"
                size="sm"
                variant="link"
                className="text-decoration-none text-500 px-1"
                onClick={clearPromptFilters}
              >
                پاک کردن
              </Button>
            )}
        </div>
        {filtered.length > 0 && (
          <div className="order-1">
            <PromptBulkActions
              count={selectedIds.size}
              running={runningBulk}
              deleting={deletingBulk}
              onRun={() => void runSelected()}
              onDelete={requestDeleteSelected}
              className="mb-0"
            />
          </div>
        )}
      </div>

      {prompts.length === 0 && !hasActiveFilters ? (
        <Card className="border-dashed">
          <Card.Body className="py-5 text-center d-flex flex-column align-items-center">
            <p className="fs-0 fw-semibold text-800 mb-2">هنوز پرامپتی وجود ندارد</p>
            <p className="fs--1 text-500 mb-0" style={{ maxWidth: '28rem' }}>
              از AI سوالی بپرسید تا دیده‌شدن برند را در پاسخ ردیابی کنید. هر خط یک پرامپت
              جداگانه است.
            </p>
            <Button
              size="sm"
              variant="falcon-primary"
              className="mt-3 d-flex gap-2 align-items-center justify-content-center text-nowrap"
              onClick={() => setNewPromptOpen(true)}
            >
              <FontAwesomeIcon icon="plus" />
              پرامپت جدید
            </Button>
          </Card.Body>
        </Card>
      ) : filtered.length === 0 ? (
        <PromptFilterEmptyState onClear={clearPromptFilters} />
      ) : (
        <>
          <PromptsTable
            prompts={filtered}
            selectedId={selectedId}
            runningId={runningId}
            runningIds={runningPromptIds}
            showTopic={showTopic}
            loading={loading}
            onSelect={selectPrompt}
            onSelectionChange={setSelectedIds}
            onRun={(id) => void runPrompt(id)}
            onStop={forgetPromptRunBatch}
          />
        </>
      )}

      {selected && (
        <PromptInspector
          prompt={{
            id: selected.id,
            text: selected.text,
            status:
              selected.status === 'executed' || selected.status === 'partial'
                ? 'completed'
                : selected.status === 'draft'
                  ? 'draft'
                  : selected.status,
            responseText: null,
            executedAt: selected.lastRunAt,
            modelId: selected.modelId,
            model: selected.providers[0]?.publicName ?? null,
            providers: selected.providers,
            runsCount: selected.runs,
            brands: [],
          }}
          topicName={showTopic ? (selected as ProjectPrompt).topicName : undefined}
          running={runningId === selected.id || runningPromptIds.includes(selected.id)}
          duplicateExecution={duplicateNotice}
          onViewDuplicateResult={viewDuplicateResult}
          onRun={() => void runPrompt(selected.id)}
          onClose={closeInspector}
          projectId={projectId ?? undefined}
          onAddCompetitor={handleAddCompetitor}
          onCreateCompetitor={handleCreateCompetitor}
          brandRows={getPromptBrandRows(selected)}
        />
      )}

      <CancelModal
        show={pendingDelete != null}
        setShow={() => setPendingDelete(null)}
        title="حذف پرامپت"
        submitTitle="آره، حذف بشه"
        isSubmitting={deletingBulk}
        submitFunc={confirmDelete}
        desc={`آیا مطمئن هستید می‌خواهید ${selectedIds.size} پرامپت انتخاب‌شده حذف شود؟ این عمل قابل بازگشت نیست.`}
      />
    </div>
  );
}

export default PromptsPage;
